import React, { useState, useEffect } from 'react';
import { usersAPI } from '../lib/api';
import { formatDate } from '../lib/dateUtils';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Users, Mail, Shield, Calendar, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { usePagination, PaginationBar } from '../components/PaginationBar';

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState({ open: false, user: null });
  const [formData, setFormData] = useState({ name: '', email: '', role: '' });
  const [createModal, setCreateModal] = useState(false);
  const [createData, setCreateData] = useState({ name: '', email: '', password: '', role: 'coordinator' });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Admin access required');
      } else {
        toast.error('Failed to fetch users');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setFormData({ name: user.name, email: user.email, role: user.role });
    setEditModal({ open: true, user });
  };

  const handleSave = async () => {
    try {
      await usersAPI.update(editModal.user.id, formData);
      toast.success('User updated successfully');
      setEditModal({ open: false, user: null });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDelete = async (userId) => {
    if (userId === currentUser.id) {
      toast.error('You cannot delete your own account');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await usersAPI.delete(userId);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleCreate = async () => {
    if (!createData.name.trim() || !createData.email.trim() || !createData.password) {
      toast.error('Name, email and password are required');
      return;
    }
    if (createData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setCreating(true);
    try {
      await usersAPI.create(createData);
      toast.success(`${createData.name} added as ${createData.role}`);
      setCreateModal(false);
      setCreateData({ name: '', email: '', password: '', role: 'coordinator' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const getRoleBadge = (role) => {
    const variants = {
      admin: 'bg-ica-navy/10 text-ica-navy border-ica-navy/20',
      coordinator: 'bg-ica-gold/10 text-ica-gold-dark border-ica-gold/20',
      support: 'bg-sky-100 text-sky-800 border-sky-200',
    };
    const labels = { admin: 'Administrator', coordinator: 'Coordinator', support: 'Support Staff' };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${variants[role] || variants.coordinator}`}>
        {labels[role] || 'Coordinator'}
      </span>
    );
  };

  const pg = usePagination(users, 'users');

  if (currentUser?.role !== 'admin') {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-64">
          <Shield className="w-12 h-12 text-gray-300 mb-4" />
          <p className="text-gray-500">Admin access required to view this page.</p>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ica-navy"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="user-management-page" className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-ica-navy">User Management</h1>
            <p className="text-gray-500 mt-1">Manage system users and their roles</p>
          </div>
          <Button
            data-testid="add-user-btn"
            onClick={() => setCreateModal(true)}
            
          >
            + New User
          </Button>
        </div>

        {/* Users Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-ica-navy">
              <Users className="w-5 h-5" />
              System Users ({users.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <PaginationBar {...pg.bar} position="top" noun="users" />
            <div className="table-scroll">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-ica-navy uppercase tracking-wider">User</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-ica-navy uppercase tracking-wider">Email</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-ica-navy uppercase tracking-wider">Role</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-ica-navy uppercase tracking-wider">Created</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-ica-navy uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pg.pageItems.map((user) => (
                    <tr key={user.id} data-testid={`user-row-${user.id}`} className="border-b hover:bg-ica-gold/5 transition-colors">
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-ica-navy/10 rounded-full flex items-center justify-center text-ica-navy font-semibold">
                            {user.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{user.name}</p>
                            {user.id === currentUser.id && (
                              <span className="text-xs text-ica-gold">(You)</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2 text-gray-600">
                          <Mail className="w-4 h-4 text-gray-400" />
                          {user.email}
                        </div>
                      </td>
                      <td className="py-4 px-4">{getRoleBadge(user.role)}</td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Calendar className="w-4 h-4" />
                          {formatDate(user.created_at)}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex gap-1">
                          <button
                            data-testid={`edit-user-${user.id}`}
                            onClick={() => handleEdit(user)}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-600"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          {user.id !== currentUser.id && (
                            <button
                              data-testid={`delete-user-${user.id}`}
                              onClick={() => handleDelete(user.id)}
                              className="p-2 hover:bg-red-50 text-red-500 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationBar {...pg.bar} position="bottom" noun="users" />
          </CardContent>
        </Card>

        {/* Create User Modal */}
        <Dialog open={createModal} onOpenChange={setCreateModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-ica-navy">Create New User</DialogTitle>
              <DialogDescription className="text-gray-500">
                Create an account. Choose <strong>Support Staff</strong> for read-only viewers.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Name</Label>
                <Input
                  data-testid="create-user-name"
                  value={createData.name}
                  onChange={(e) => setCreateData({ ...createData, name: e.target.value })}
                  placeholder="Full name"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  data-testid="create-user-email"
                  type="email"
                  value={createData.email}
                  onChange={(e) => setCreateData({ ...createData, email: e.target.value })}
                  placeholder="user@example.com"
                />
              </div>
              <div>
                <Label>Password</Label>
                <Input
                  data-testid="create-user-password"
                  type="password"
                  value={createData.password}
                  onChange={(e) => setCreateData({ ...createData, password: e.target.value })}
                  placeholder="At least 8 characters"
                />
              </div>
              <div>
                <Label>Role</Label>
                <Select value={createData.role} onValueChange={(v) => setCreateData({ ...createData, role: v })}>
                  <SelectTrigger data-testid="create-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Administrator</SelectItem>
                    <SelectItem value="coordinator">Coordinator</SelectItem>
                    <SelectItem value="support">Support Staff (read-only)</SelectItem>
                  </SelectContent>
                </Select>
                {createData.role === 'support' && (
                  <p className="text-[11px] text-sky-700 mt-1.5">
                    Support Staff can view every placement, restaurant and document but cannot create, edit, delete or send anything.
                  </p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateModal(false)}>Cancel</Button>
              <Button
                data-testid="submit-create-user-btn"
                onClick={handleCreate}
                disabled={creating}
                
              >
                {creating ? 'Creating…' : 'Create User'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit User Modal */}
        <Dialog open={editModal.open} onOpenChange={(open) => setEditModal({ ...editModal, open })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-ica-navy">Edit User</DialogTitle>
              <DialogDescription className="text-gray-500">
                Update user information and role.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Name</Label>
                <Input
                  data-testid="edit-user-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  data-testid="edit-user-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              <div>
                <Label>Role</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                  <SelectTrigger data-testid="edit-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Administrator</SelectItem>
                    <SelectItem value="coordinator">Coordinator</SelectItem>
                    <SelectItem value="support">Support Staff (read-only)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditModal({ open: false, user: null })}>
                Cancel
              </Button>
              <Button
                data-testid="save-user-btn"
                onClick={handleSave}
                
              >
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default UserManagement;
